import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { login, logout, register, getCurrentUser } from '../services/auth'
import type { LoginPayload, RegisterPayload } from '../types'
import { clearAuthToken, hasAuthToken, setAuthToken } from '../utils/authToken'

export function useCurrentUser() {
  return useQuery({
    queryKey: ['currentUser'],
    queryFn: getCurrentUser,
    // hasAuthToken() is SSR-safe — returns false during prerender so the
    // query stays disabled and we never touch localStorage server-side.
    enabled: hasAuthToken(),
    retry: false,
    staleTime: 5 * 60 * 1000, // re-use cached user for 5 min before re-fetching
  })
}

export function useRegister() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: RegisterPayload) => register(payload),
    onSuccess: (data) => {
      setAuthToken(data.token)
      queryClient.setQueryData(['currentUser'], data.user)
    },
  })
}

export function useLogin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: LoginPayload) => login(payload),
    onSuccess: (data) => {
      setAuthToken(data.token)
      queryClient.setQueryData(['currentUser'], data.user)
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: logout,
    onSettled: () => {
      clearAuthToken()
      queryClient.clear()
    },
  })
}
